import { useEffect, useRef, useState } from 'react'

import { motion } from 'framer-motion'

const MILESTONES = [0, 25, 50, 75, 98, 100]
const VIDEO_TRACKING_INTERVAL = 5 * 60 * 1000 // 5 minutes

const PROTECTED_CONTROLS_LIST = 'nodownload noremoteplayback'
const PROTECTED_ATTRIBUTES: Record<string, string> = {
  controlslist: PROTECTED_CONTROLS_LIST,
  disablepictureinpicture: '',
  disableremoteplayback: '',
  draggable: 'false',
}

interface VideoPlayerProps {
  src: string
  onTrackAction?: () => void
  onPercentageWatchedMilestone?(milestone: number, video: HTMLVideoElement): void
  disableAdvancedControls?: boolean
}

export default function VideoPlayer({
  src,
  onTrackAction,
  onPercentageWatchedMilestone,
  disableAdvancedControls = false,
}: VideoPlayerProps) {
  const videoRef = useRef<HTMLVideoElement>(null)
  const milestonesRef = useRef<Set<number>>(new Set())
  const callbackRef = useRef(onPercentageWatchedMilestone)
  const trackActionRef = useRef(onTrackAction)
  const [videoLoaded, setVideoLoaded] = useState(false)
  const intervalRef = useRef<NodeJS.Timeout | null>(null)

  callbackRef.current = onPercentageWatchedMilestone
  trackActionRef.current = onTrackAction

  // Reset milestones and video loaded state when src changes
  useEffect(() => {
    milestonesRef.current.clear()
    setVideoLoaded(false)
  }, [src])

  // Restore protected attributes if removed via DevTools
  useEffect(() => {
    const video = videoRef.current
    if (!video || !disableAdvancedControls) return

    const observer = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        if (mutation.type !== 'attributes') continue
        const attr = mutation.attributeName
        if (attr && attr in PROTECTED_ATTRIBUTES) {
          const expected = PROTECTED_ATTRIBUTES[attr] ?? ''
          if (!video.hasAttribute(attr) || video.getAttribute(attr) !== expected) {
            video.setAttribute(attr, expected)
          }
        }
      }
    })

    observer.observe(video, {
      attributes: true,
      attributeFilter: Object.keys(PROTECTED_ATTRIBUTES),
    })

    return () => observer.disconnect()
  }, [disableAdvancedControls])

  const handleContextMenu = (e: React.MouseEvent) => {
    if (disableAdvancedControls) {
      e.preventDefault()
    }
  }

  const clearTrackingInterval = () => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current)
      intervalRef.current = null
    }
  }

  // Track user actions (play, pause, ended) with periodic interval during playback
  useEffect(() => {
    const video = videoRef.current
    if (!video) return

    const handlePlay = () => {
      trackActionRef.current?.()
      clearTrackingInterval()
      intervalRef.current = setInterval(() => {
        trackActionRef.current?.()
      }, VIDEO_TRACKING_INTERVAL)
    }

    const handlePause = () => {
      trackActionRef.current?.()
      clearTrackingInterval()
    }

    const handleEnded = () => {
      clearTrackingInterval()
    }

    video.addEventListener('play', handlePlay)
    video.addEventListener('pause', handlePause)
    video.addEventListener('ended', handleEnded)

    return () => {
      clearTrackingInterval()
      video.removeEventListener('play', handlePlay)
      video.removeEventListener('pause', handlePause)
      video.removeEventListener('ended', handleEnded)
    }
  }, [])

  useEffect(() => {
    const video = videoRef.current
    if (!video) return

    const handleTimeUpdate = () => {
      if (!video.duration || !callbackRef.current) return

      const progress = Math.round((video.currentTime / video.duration) * 100)
      for (const milestone of MILESTONES) {
        if (milestonesRef.current.has(milestone)) continue
        if (progress >= milestone) {
          milestonesRef.current.add(milestone)
          callbackRef.current(milestone, video)
        }
      }
    }

    video.addEventListener('timeupdate', handleTimeUpdate)
    return () => {
      video.removeEventListener('timeupdate', handleTimeUpdate)
    }
  }, [])

  return (
    <motion.video
      ref={videoRef}
      controls
      controlsList={disableAdvancedControls ? PROTECTED_CONTROLS_LIST : undefined}
      disablePictureInPicture={disableAdvancedControls}
      disableRemotePlayback={disableAdvancedControls}
      draggable={!disableAdvancedControls}
      onContextMenu={handleContextMenu}
      src={src}
      initial={{ opacity: 0 }}
      animate={{ opacity: videoLoaded ? 1 : 0 }}
      transition={{ duration: 0.3 }}
      onLoadedData={() => setVideoLoaded(true)}
      style={{
        width: '100%',
        height: '100%',
        objectFit: 'contain',
        display: 'block',
      }}
    />
  )
}
